import { organizationBasicSchema } from '@/fetching/sharedSchemas/basicSchemas';
import { atomWithLocalStorage } from '@/utils/browserStorage';
import { useAtomValue } from 'jotai';
import { z } from 'zod';

// The atom holding the authenticated user's state, persisted to localStorage
//  (and therefore synced across tabs via the `storage` event).
//
// This is deliberately NOT exported from authentication.ts: app code should
//  only ever read it through the getter hooks there, and only ever clear it
//  through useClearAuthenticationState() / clearAuthenticationState(). It
//  lives in this minimal module (instead of authentication.ts) so non-React
//  code like clearAuthState.ts can import it without pulling in the
//  React-heavy authentication module.
export const authStateAtom = atomWithLocalStorage(
    'authenticatedUser',
    null,
    z
        .object({
            // Optional for backwards compatibility: sessions authenticated before
            //  the backend started sending the user UUID have no `uuid` stored, and
            //  a required field here would fail schema validation, wiping the auth
            //  atom and logging the user out.
            uuid: z.string().optional(),
            email: z.string(),
            organization: organizationBasicSchema,
            // Optional: a session predating these fields has nothing stored, and
            //  requiring them would fail validation and log the user out.
            firstName: z.string().optional(),
            lastName: z.string().optional(),
            // The address the digest signs, which is the one the backend holds rather
            //  than whatever was typed at the sign-in form.
            pylonEmail: z.string().optional(),
            pylonEmailHash: z.string().optional(),
        })
        .nullable(),
);

// Returns whether the user is authenticated.
//  This is the source of truth for protecting the authenticated routes.
//
// It lives alongside the atom, rather than with the other getters in
//  authentication.ts, so that modules which only need to know whether a user is
//  authenticated can read it without importing the React-heavy authentication
//  module, which imports the fetching layer and would otherwise close an import
//  cycle back on itself.
export const useIsAuthenticated = () => !!useAtomValue(authStateAtom);
